/**
 * PDF 的第一頁 → JPEG 縮圖。**在瀏覽器算繪**。
 *
 * ⚠ **為什麼在前端做。** 後端沒有能讀 PDF 的東西：`sharp` 的預編譯 binary 不含
 * libvips+poppler，`jspdf` / `pdfkit` 是產生 PDF 而不是讀。要在後端做就得多一個
 * 原生依賴進部署 image，而 ffmpeg 那件事還沒完成（B0）。
 *
 * ⚠ **走 `vue-pdf` 而不是直接 import `pdfjs-dist`。** `pdfjs-dist` 在這個專案是
 * `vue-pdf` 的**傳遞依賴**（不在 package.json 裡），直接 import 它會在 vue-pdf
 * 改版時無聲壞掉。而且 `vue-pdf` 匯入時就把 worker 設好了
 * （`worker-loader!pdfjs-dist/es5/build/pdf.worker.js`）——那段 webpack 設定
 * 已經被 `Print.vue` 證明可用，自己接 pdfjs 要重做一次。
 *
 * ⚠ **動態 import，但要知道它現在省不到什麼。** `Print.vue` 已經**靜態** import
 * `vue-pdf`，所以 pdfjs 早就在 `chunk-vendors` 裡了（build 產出的那個 870KB 的
 * `*.worker.js` 就是它）——這支檔案實測只讓 bundle 多 6KB。
 * 保留動態 import 的理由是形狀正確、成本為零：哪天 `Print.vue` 改成 lazy route，
 * 素材庫就不會反過來把 pdfjs 拉回同步 chunk。**不要因為「反正已經在裡面」改成靜態。**
 */

/** 縮圖的長邊。挑選器的縮圖框大約 200px 寬，2 倍給高解析螢幕。 */
const POSTER_MAX_EDGE = 1000;
/** JPEG 品質。0.8 在 1000px 寬大約 40–120KB。 */
const POSTER_QUALITY = 0.8;

/**
 * @param {File} file 使用者選的 PDF
 * @returns {Promise<File|null>} JPEG 檔；算繪失敗回 `null`（呼叫端要照樣送出上傳）
 */
export async function renderPdfPoster(file) {
    try {
        /// ⚠ 這一行是動態 import 的重點，不要改成頂層 import。
        const mod = await import('vue-pdf');
        const vuePdf = mod.default || mod;

        const buffer = await file.arrayBuffer();
        /// ⚠ `createLoadingTask` 收 `Uint8Array` 或 `{ data }`；給 File 物件不行。
        const task = vuePdf.createLoadingTask(new Uint8Array(buffer));
        const doc = await task.promise;
        const page = await doc.getPage(1);

        /// scale 1 的 viewport 只是為了量原始尺寸，再換算成長邊 1000 的比例
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(base.width, base.height));
        const viewport = page.getViewport({ scale: scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));
        const ctx = canvas.getContext('2d');
        /// ⚠ **先填白。** PDF 的背景是透明的，而 JPEG 沒有 alpha——不填白的話
        /// 透明的地方會變成黑色，整張縮圖看起來像壞的。
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({ canvasContext: ctx, viewport: viewport }).promise;

        const blob = await new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', POSTER_QUALITY);
        });
        if (!blob) {
            return null;
        }
        /// 檔名不重要（後端自己決定 S3 的 key），但要有 .jpg——後端的副檔名白名單看它
        return new File([blob], 'poster.jpg', { type: 'image/jpeg' });
    } catch (err) {
        /// ⚠ **一律不 throw。** 縮圖只是為了讓挑選器認得出這是哪一份學習單，
        /// 沒有它只是退回圖示 + 檔名。加密的 PDF、壞掉的檔、瀏覽器不支援，
        /// 都應該讓上傳照常進行。
        console.log(`failed to render pdf poster: ${err}`);
        return null;
    }
}
